import React, { useState, useEffect } from 'react';
import { Card, Timeline, Tag, Progress, Space, Typography, Badge, Button } from 'antd';
import { 
  EyeOutlined, 
  ThunderboltOutlined, 
  SafetyOutlined, 
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  RobotOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

interface ThreatDetection {
  id: string;
  timestamp: string;
  type: 'behavior' | 'network' | 'location' | 'pattern';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  confidence: number;
  autoResolved: boolean;
}

export const AIRealTimeMonitor: React.FC = () => {
  const [detections, setDetections] = useState<ThreatDetection[]>([]);
  const [isMonitoring, setIsMonitoring] = useState(true);
  const [processingSpeed, setProcessingSpeed] = useState(1247);

  useEffect(() => {
    // Simulate real-time AI detections
    const interval = setInterval(() => {
      if (isMonitoring && Math.random() > 0.6) {
        const newDetection: ThreatDetection = {
          id: Date.now().toString(),
          timestamp: new Date().toISOString(),
          type: ['behavior', 'network', 'location', 'pattern'][Math.floor(Math.random() * 4)] as any,
          severity: Math.random() > 0.8 ? 'critical' : Math.random() > 0.6 ? 'high' : 'medium',
          description: [
            'Anomalous user behavior pattern detected',
            'Suspicious network activity identified',
            'Irregular location access pattern',
            'Unusual data access sequence found',
            'Emergency alert pattern recognized',
            'Coordinated activity detected'
          ][Math.floor(Math.random() * 6)],
          confidence: Math.random() * 0.3 + 0.7, // 70-100%
          autoResolved: Math.random() > 0.7
        };
        
        setDetections(prev => [newDetection, ...prev.slice(0, 9)]);
        setProcessingSpeed(prev => prev + Math.floor(Math.random() * 50));
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isMonitoring]);

  // Initial mock data
  useEffect(() => {
    setDetections([
      {
        id: '1',
        timestamp: new Date(Date.now() - 120000).toISOString(),
        type: 'behavior',
        severity: 'high',
        description: 'Multiple failed authentication attempts from same IP',
        confidence: 0.94,
        autoResolved: true
      },
      {
        id: '2',
        timestamp: new Date(Date.now() - 300000).toISOString(),
        type: 'pattern',
        severity: 'medium',
        description: 'Unusual emergency call frequency in Kadıköy region',
        confidence: 0.87,
        autoResolved: false
      },
      {
        id: '3',
        timestamp: new Date(Date.now() - 480000).toISOString(),
        type: 'location',
        severity: 'critical',
        description: 'Emergency cluster detected - potential incident',
        confidence: 0.96,
        autoResolved: false
      }
    ]);
  }, []);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#ff7a45';
      case 'medium': return '#ffa940';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'behavior': return <EyeOutlined />;
      case 'network': return <ThunderboltOutlined />;
      case 'location': return <SafetyOutlined />;
      case 'pattern': return <WarningOutlined />;
      default: return <RobotOutlined />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'behavior': return 'Davranış';
      case 'network': return 'Ağ';
      case 'location': return 'Konum';
      case 'pattern': return 'Kalıp';
      default: return 'Diğer';
    }
  };

  return (
    <Card 
      title={
        <Space>
          <RobotOutlined style={{ color: '#1890ff' }} />
          <span>🤖 Gerçek Zamanlı AI Tespiti</span>
          <Badge 
            status={isMonitoring ? "processing" : "default"} 
            text={isMonitoring ? "İzleniyor" : "Duraklatıldı"}
          />
        </Space>
      }
      extra={
        <Button 
          size="small" 
          type={isMonitoring ? "default" : "primary"}
          onClick={() => setIsMonitoring(!isMonitoring)}
          icon={isMonitoring ? <CheckCircleOutlined /> : <ReloadOutlined />}
        >
          {isMonitoring ? 'Duraklat' : 'Devam Et'}
        </Button>
      }
      style={{ 
        height: '600px',
        '@media (max-width: 768px)': {
          height: '500px'
        }
      }}
    >
      {/* AI Performance Stats */}
      <div style={{ marginBottom: 16, padding: 12, background: '#f6ffed', borderRadius: 6 }}>
        <Space split={<span style={{ color: '#d9d9d9' }}>|</span>}>
          <div>
            <Text type="secondary">İşlem Hızı</Text>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
              {processingSpeed.toLocaleString()}/dk
            </div>
          </div>
          <div>
            <Text type="secondary">Aktif Modeller</Text>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
              5/5 Hazır
            </div>
          </div>
          <div>
            <Text type="secondary">Güven Oranı</Text>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>
              94.7%
            </div>
          </div>
        </Space>
      </div>

      {/* Real-time Detection Timeline */}
      <div style={{ height: '480px', overflowY: 'auto' }}>
        <Timeline
          items={detections.map(detection => ({
            key: detection.id,
            dot: React.createElement(
              getTypeIcon(detection.type).type,
              { 
                style: { 
                  color: getSeverityColor(detection.severity),
                  fontSize: '14px'
                }
              }
            ),
            children: (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <Tag color={getSeverityColor(detection.severity)} style={{ color: 'white' }}>
                      {detection.severity.toUpperCase()}
                    </Tag>
                    <Tag>{getTypeLabel(detection.type)}</Tag>
                    {detection.autoResolved && (
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        Auto-Resolved
                      </Tag>
                    )}
                  </Space>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {new Date(detection.timestamp).toLocaleTimeString('tr-TR')}
                  </Text>
                </div>
                <div style={{ marginTop: 4 }}>
                  <Text style={{ fontSize: '13px' }}>{detection.description}</Text>
                </div>
                <div style={{ marginTop: 8 }}>
                  <Progress 
                    percent={Math.round(detection.confidence * 100)} 
                    size="small"
                    strokeColor={getSeverityColor(detection.severity)}
                    format={percent => `${percent}% confidence`}
                  />
                </div>
              </div>
            )
          }))}
        />
      </div>
      
      {detections.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          <RobotOutlined style={{ fontSize: '32px', marginBottom: 8 }} />
          <div>AI izliyor... Tehdit tespit edilmedi</div>
        </div>
      )}
    </Card>
  );
};
